/**
 * API endpoint definitions and utilities for V4 Dashboard
 */

import { DashboardQueryParams } from '../types'

/**
 * Parameter formatting strategy for different API endpoints
 */
type ParameterFormat = 'array' | 'comma-separated'

/**
 * Parameter name mapping for different endpoints
 */
interface ParameterMapping {
  userIDs: string
  categories: string
  licenseTypes: string
  groupIDs: string
}

/**
 * Base API endpoints for V4 dashboard
 */
export let API_ENDPOINTS = {
  TIMELINE_ACTIVITIES: '/api/manage/analytics/timeline-activities',
  ASSIGNMENT_EVENTS: '/api/manage/analytics/assignment-events',
  OVERVIEW: '/api/manage/analytics/overview',
  SUMMARY: '/api/manage/dashboard/summary',
  ANALYTICS: '/api/manage/dashboard/analytics',
} as const

/**
 * Shared helper to build query strings with different parameter formats
 */
function buildQueryStringWithStrategy(
  params: DashboardQueryParams,
  format: ParameterFormat,
  paramMapping: ParameterMapping,
): string {
  let searchParams = new URLSearchParams()

  // Required parameters (same for all endpoints)
  searchParams.append('linkedServiceID', params.linkedServiceID)
  searchParams.append('startDate', params.startDate)
  searchParams.append('endDate', params.endDate)

  // Optional array parameters with different formatting strategies
  if (params.userIDs?.length) {
    if (format === 'array') {
      params.userIDs.forEach((id) =>
        searchParams.append(paramMapping.userIDs, id),
      )
    } else {
      searchParams.append(paramMapping.userIDs, params.userIDs.join(','))
    }
  }

  if (params.categories?.length) {
    if (format === 'array') {
      params.categories.forEach((category) =>
        searchParams.append(paramMapping.categories, category),
      )
    } else {
      searchParams.append(paramMapping.categories, params.categories.join(','))
    }
  }

  if (params.licenseTypes?.length) {
    if (format === 'array') {
      params.licenseTypes.forEach((type) =>
        searchParams.append(paramMapping.licenseTypes, type),
      )
    } else {
      searchParams.append(
        paramMapping.licenseTypes,
        params.licenseTypes.join(','),
      )
    }
  }

  if (params.groupIDs?.length) {
    if (format === 'array') {
      params.groupIDs.forEach((id) =>
        searchParams.append(paramMapping.groupIDs, id),
      )
    } else {
      searchParams.append(paramMapping.groupIDs, params.groupIDs.join(','))
    }
  }

  return searchParams.toString()
}

/**
 * Build query string from dashboard query parameters (array format)
 */
export function buildQueryString(params: DashboardQueryParams): string {
  return buildQueryStringWithStrategy(params, 'array', {
    userIDs: 'userIDs[]',
    categories: 'categories[]',
    licenseTypes: 'licenseTypes[]',
    groupIDs: 'groupIDs[]',
  })
}

/**
 * Build query string for analytics endpoint (comma-separated format)
 */
export function buildAnalyticsQueryString(
  params: DashboardQueryParams,
): string {
  return buildQueryStringWithStrategy(params, 'comma-separated', {
    userIDs: 'users',
    categories: 'categories',
    licenseTypes: 'licenseTypes',
    groupIDs: 'groups',
  })
}

/**
 * Build full API URL with query parameters
 */
export function buildApiUrl(
  endpoint: string,
  params: DashboardQueryParams,
): string {
  let queryString = buildQueryString(params)
  return `${endpoint}?${queryString}`
}

/**
 * Build analytics API URL with query parameters
 */
export function buildAnalyticsUrl(params: DashboardQueryParams): string {
  let queryString = buildAnalyticsQueryString(params)
  return `${API_ENDPOINTS.ANALYTICS}?${queryString}`
}
